/**
 * `GET /v1/search/you-may-like` → карточки полки вкуса.
 *
 * ПОЛКА СКРЫВАЕТСЯ ЦЕЛИКОМ, КОГДА ВКУС НЕ ПРИМЕНЁН. `applied: false` означает,
 * что до запроса вкус не дошёл: покупательница не вошла, профиль слишком тонкий,
 * или случился сбой — и полка тогда просто топ по качеству. Рисовать её под
 * заголовком, обещающим персональную подборку, значит обещать то, чего в ней нет.
 * Прячем ЗАГОЛОВОК ВМЕСТЕ С КАРТОЧКАМИ, а не показываем пустую секцию.
 *
 * ДЕМО-ЗАПАСКИ ЗДЕСЬ НЕТ НАМЕРЕННО. Старая полка при пустом ответе подставляла
 * демонстрационные карточки (`allowDemoFallback`), и именно так выглядел баг с
 * recently-viewed: четыре одинаковых заглушки на месте живых товаров, и никто не
 * замечал, что ручка молчит. Пустой ответ — это пустая полка, которой не видно.
 */

import type { ProductTeaser, YouMayLikeResponse } from '@/features/search/api/searchV1'
import type {
  FairProductV2RecsCard,
  FairProductV2RecsColorDot,
} from '@/widgets/product/fair-v2/fair-product-v2.data'
import type { FairSearchV2ProductCard } from './fair-search-v2.data'
import { priceFreshnessFields } from '@/features/price-freshness'

export interface FairSearchV2YouMayLikeView {
  /** Рисовать ли секцию вообще — вместе с заголовком. */
  visible: boolean
  cards: FairProductV2RecsCard[]
  /** Тот же процент, что в плашке: одно число из одного места. */
  score: number
}

const HIDDEN: FairSearchV2YouMayLikeView = { visible: false, cards: [], score: 0 }

/**
 * Цена как строка суммы: карточка сама добавляет «from».
 *
 * ЦЕНА — ЭТО `amount`, И ТОЛЬКО ОН (FE-049).
 *
 * Раньше здесь стояло `price.discount_amount ?? price.amount`, то есть поле со
 * словом «discount» в имени принималось за цену со скидкой. На деле это цена
 * ДО скидки — у каждого найденного на проде товара она БОЛЬШЕ `amount`:
 *
 *     Ferragamo Chelsea boots   amount 922.42   discount_amount 1888.83
 *     ETRO paisley skirt        amount 1145.49  discount_amount 1908.25
 *
 * Сверено с карточкой товара: `GET /v1/product/133828/card` →
 * `pricing.from_amount = 922.42`. Выдача показывала 1 889 за вещь, которая
 * стоит 922, — и на запросе «sale», где скидка у ВСЕХ, врали все карточки
 * сразу. Имя поля заведено бэку на переименование (**BE-110**).
 *
 * Старая цена зачёркиванием здесь НЕ рисуется: у карточки выдачи нет такого
 * свойства, и макета на него я не видел. Это отдельная задача, а не повод
 * продолжать показывать неверное число.
 *
 * Пустая строка вместо нуля: карточка без цены не должна обещать «$0».
 */
function formatAmount(teaser: ProductTeaser): string {
  const price = teaser.price_from
  if (!price || price.amount == null) return ''
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: price.currency || 'USD',
    maximumFractionDigits: 0,
  }).format(price.amount)
}

/**
 * Есть ли у товара скидка (FE-051).
 *
 * Признак — непустая цена ДО скидки, которая БОЛЬШЕ текущей. Проверка на «>»
 * не придирка: поле названо так, что в него легко положить что угодно (см.
 * BE-110), и равная или меньшая величина скидкой не является. Лучше не
 * показать бирку, чем объявить скидкой round-trip цены.
 */
function hasDiscount(teaser: ProductTeaser): boolean {
  const price = teaser.price_from
  if (!price || price.amount == null) return false
  // BE-110: `price_without_discount` is the list price under its honest name;
  // the deprecated `discount_amount` (same number) is read for one release.
  const before = price.price_without_discount ?? price.discount_amount
  return before != null && before > price.amount
}

/**
 * Свотчи, которые МОЖНО показать.
 *
 * Чип без hex отбрасывается, а не превращается в серую заглушку. Измерено
 * 2026-08-28: ML-цвет есть у 7.5% каталога, поэтому у подавляющего большинства
 * товаров hex пуст — и плашка превращалась в ряд одинаковых серых кружков,
 * которые не говорят покупательнице ничего. Заглушка честна как признак «мы не
 * знаем», но как элемент интерфейса это шум, неотличимый от настоящего цвета.
 *
 * Не знаем — не рисуем. Имя цвета при этом сохраняется в подписи там, где оно
 * есть: словом «neutrals» можно распорядиться, серым кружком нельзя.
 */
function mapColors(teaser: ProductTeaser): FairProductV2RecsColorDot[] {
  return (teaser.color_chips ?? [])
    .filter((chip) => Boolean(chip.hex))
    .map((chip) => ({
      color: chip.hex as string,
      label: chip.name ?? undefined,
    }))
}

export function mapFairSearchV2TeaserToCard(teaser: ProductTeaser): FairProductV2RecsCard {
  return {
    id: teaser.product_id,
    variantId: teaser.variant_id,
    brand: teaser.brand ?? '',
    title: teaser.name,
    price: formatAmount(teaser),
    images: teaser.images ?? [],
    colors: mapColors(teaser),
    // честное число расцветок из ответа — карточка превращает избыток в «+N»
    colorsTotal: teaser.colors_total,
    isFavorite: teaser.is_favorite,
    discount: hasDiscount(teaser),
    ...priceFreshnessFields(teaser.price_from),
  }
}

/**
 * Тот же тизер — карточка СЕТКИ.
 *
 * Сетка и полка рисуют разные компоненты, но собираются из одного ответа одними
 * правилами: цена, картинки, свотчи и честное число расцветок. Две независимые
 * сборки разошлись бы на первом же поле, которое кто-то поправил в одном месте.
 */
export function mapFairSearchV2TeaserToGridCard(
  teaser: ProductTeaser,
): FairSearchV2ProductCard {
  return {
    id: teaser.product_id,
    variantId: teaser.variant_id,
    brand: teaser.brand ?? '',
    title: teaser.name,
    price: formatAmount(teaser),
    images: teaser.images ?? [],
    colors: mapColors(teaser),
    colorsTotal: teaser.colors_total,
    isFavorite: teaser.is_favorite,
    discount: hasDiscount(teaser),
    ...priceFreshnessFields(teaser.price_from),
  }
}

export function resolveFairSearchV2YouMayLike(
  response: YouMayLikeResponse | null | undefined,
): FairSearchV2YouMayLikeView {
  if (!response) return HIDDEN

  const applied = response.personalization?.applied === true
  if (!applied) return HIDDEN

  const cards = (response.items ?? []).map(mapFairSearchV2TeaserToCard)
  if (cards.length === 0) return HIDDEN

  return { visible: true, cards, score: response.personalization.score }
}

/**
 * Decision 11 — «Popular now» under a page whose words match nothing (`result_mode: no_match`):
 * `popular.items` (12, the tab's anonymous Popular Today). Not results — never counted; any other
 * page carries `popular: null` → no shelf.
 */
export function resolveFairSearchV2PopularNow(
  popular: { items?: ProductTeaser[] | null } | null | undefined,
): FairProductV2RecsCard[] {
  return (popular?.items ?? []).map(mapFairSearchV2TeaserToCard)
}
